import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icon';
import { participantColors as c } from '../../theme/participantTheme';
import { couponService } from '../../services/couponService';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../logo.png';

interface ParticipantTopBarProps {
  /** Mostra avatar (iniciais) + botão de logout — usado só no Dashboard. */
  showAvatarActions?: boolean;
}

/**
 * Barra superior das páginas do fluxo do participante: logo + pill de
 * contagem de cupons cadastrados. Substitui o AppHeader (usado pelo painel
 * admin) só nessas páginas.
 */
export const ParticipantTopBar: React.FC<ParticipantTopBarProps> = ({ showAvatarActions = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [couponCount, setCouponCount] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    couponService
      // Só campanha(s) ativa(s): sem isso, o contador somava cupons de
      // campanhas já encerradas também, não só da campanha em andamento.
      .getMyCoupons({ limit: 1, activeCampaignsOnly: true })
      .then((res) => {
        if (active) setCouponCount(res.total);
      })
      .catch(() => {
        if (active) setCouponCount(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const initial = user?.name?.trim()?.[0]?.toUpperCase() || '?';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        pt: '18px',
        px: '4px',
      }}
    >
      <Box
        component="img"
        src={logo}
        alt="ACIAG"
        sx={{ height: 44, width: 'auto', objectFit: 'contain' }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: c.surface,
            border: `2px solid ${c.amber}`,
            borderRadius: `${c.radius.pill}px`,
            padding: '8px 16px',
            boxShadow: c.shadows.tight,
          }}
        >
          <Icon name="confirmation_number" size={20} color={c.amber} />
          <Box
            component="span"
            sx={{ fontFamily: c.fontFamily, fontSize: 16, fontWeight: 800, color: c.textBase }}
          >
            {couponCount ?? '—'}
          </Box>
        </Box>

        {showAvatarActions && (
          <>
            <Box
              component="button"
              onClick={() => navigate('/profile')}
              title="Meu perfil"
              sx={{
                width: 46,
                height: 46,
                borderRadius: '50%',
                border: `2px solid ${c.borderHover}`,
                background: c.gradients.primaryButton,
                color: c.onDarkPrimary,
                fontFamily: c.fontFamily,
                fontWeight: 800,
                fontSize: 18,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.15s',
                '&:hover': { transform: 'scale(1.06)' },
              }}
            >
              {initial}
            </Box>
            <Box
              component="button"
              onClick={() => {
                logout();
                navigate('/login');
              }}
              title="Sair"
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: `2px solid ${c.border}`,
                background: c.surface,
                color: c.brandIcon,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="logout" size={22} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
